import { FC, useMemo, useState } from 'react';

import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import {
  CHANNEL_KEY_UNSPECIFIED,
  buildChannelStats,
  fmt,
  getNiceScale,
} from './analysisUtils';

interface Props {
  orderList: GiftOrderForAdmin[] | null;
}

export const ChannelTab: FC<Props> = function ChannelTab({ orderList }) {
  const [channelHover, setChannelHover] = useState<{
    channel: string;
    x: number;
    y: number;
  } | null>(null);
  const [channelChartView, setChannelChartView] = useState<'donut' | 'bar'>(
    'donut',
  );

  const channelData = useMemo(() => {
    if (!orderList) return null;
    return buildChannelStats(orderList);
  }, [orderList]);

  if (!channelData || channelData.total === 0) {
    return (
      <div className="rounded-lg bg-white p-8 text-center shadow">
        <p className="text-gray-500">해당 기간에 쿠폰 데이터가 없습니다.</p>
      </div>
    );
  }

  const sentChannels = channelData.sentChannels;
  const channelCountScale = getNiceScale(
    Math.max(...sentChannels.map((c) => c.count), 1),
  );

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">전체 쿠폰</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {fmt(channelData.total)}장
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">미발송</p>
          <p className="mt-1 text-2xl font-bold text-gray-500">
            {fmt(channelData.notSharedTotal)}장
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {channelData.total > 0
              ? (
                  (channelData.notSharedTotal / channelData.total) *
                  100
                ).toFixed(1)
              : '0.0'}
            %
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">발송 완료</p>
          <p className="mt-1 text-2xl font-bold text-green-600">
            {fmt(channelData.sharedTotal)}장
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {channelData.sharedRate.toFixed(1)}%
          </p>
        </div>
        {sentChannels
          .filter((ch) => ch.channel !== CHANNEL_KEY_UNSPECIFIED)
          .slice(0, 2)
          .map((ch) => (
            <div key={ch.channel} className="rounded-lg bg-white p-4 shadow">
              <p className="text-xs text-gray-500">{ch.label}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {fmt(ch.count)}장
              </p>
              <p className="mt-1 text-xs text-gray-400">
                발송 대비 {ch.rate.toFixed(1)}%
              </p>
            </div>
          ))}
      </div>

      {/* 채널 비율 (도넛 / 막대 토글) */}
      <div className="flex flex-col rounded-lg bg-white p-6 shadow">
        <div className="mb-1 flex items-start justify-between gap-2">
          <h2 className="text-lg font-bold text-gray-800">발송 채널 비율</h2>
          <div className="flex shrink-0 rounded-md bg-gray-100 p-0.5">
            <button
              onClick={() => setChannelChartView('donut')}
              className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                channelChartView === 'donut'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              원형
            </button>
            <button
              onClick={() => setChannelChartView('bar')}
              className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                channelChartView === 'bar'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              막대
            </button>
          </div>
        </div>
        <p className="mb-4 text-xs text-gray-400">
          발송된 쿠폰 {fmt(channelData.sharedTotal)}장의 마지막 발송 채널 기준
        </p>
        <div className="flex min-h-[360px] flex-col">
          {channelChartView === 'donut' ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-8 lg:flex-row lg:items-center lg:justify-around">
              {/* 도넛 */}
              <div
                className="relative shrink-0"
                style={{ width: 300, height: 300 }}
              >
                <svg
                  viewBox="0 0 300 300"
                  className="h-full w-full -rotate-90"
                >
                  {(() => {
                    const r = 112;
                    const c = 2 * Math.PI * r;
                    let acc = 0;
                    return sentChannels.map((ch) => {
                      if (ch.rate <= 0) return null;
                      const seg = (ch.rate / 100) * c;
                      const offset = -(acc / 100) * c;
                      acc += ch.rate;
                      return (
                        <circle
                          key={ch.channel}
                          cx={150}
                          cy={150}
                          r={r}
                          fill="none"
                          stroke={ch.hex}
                          strokeWidth={40}
                          strokeDasharray={`${seg} ${c}`}
                          strokeDashoffset={offset}
                          className="cursor-pointer transition-opacity hover:opacity-80"
                          onMouseMove={(e) => {
                            const svg = e.currentTarget.ownerSVGElement;
                            if (!svg) return;
                            const rect = svg.getBoundingClientRect();
                            setChannelHover({
                              channel: ch.channel,
                              x: e.clientX - rect.left,
                              y: e.clientY - rect.top,
                            });
                          }}
                          onMouseLeave={() => setChannelHover(null)}
                        />
                      );
                    });
                  })()}
                </svg>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[11px] text-gray-400">발송 완료</span>
                  <span className="text-lg font-bold text-gray-900">
                    {fmt(channelData.sharedTotal)}장
                  </span>
                </div>
                {channelHover &&
                  (() => {
                    const ch = sentChannels.find(
                      (c) => c.channel === channelHover.channel,
                    );
                    if (!ch) return null;
                    return (
                      <div
                        className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded bg-gray-800 px-2 py-1 text-[11px] leading-tight whitespace-nowrap text-white shadow"
                        style={{
                          left: channelHover.x,
                          top: channelHover.y - 8,
                        }}
                      >
                        {ch.label} {ch.rate.toFixed(1)}% · {fmt(ch.count)}장
                      </div>
                    );
                  })()}
              </div>
              {/* 범례 */}
              <div className="w-full max-w-sm space-y-2">
                {sentChannels.map((ch) => (
                  <div
                    key={ch.channel}
                    className="flex items-center gap-2 text-sm"
                  >
                    <span
                      className="inline-block h-3 w-3 shrink-0 rounded-sm"
                      style={{ backgroundColor: ch.hex }}
                    />
                    <span className="w-20 shrink-0 text-gray-600">
                      {ch.label}
                    </span>
                    <span className="flex-1 text-right font-bold text-gray-800">
                      {ch.rate.toFixed(1)}%
                    </span>
                    <span className="w-16 shrink-0 text-right text-gray-600">
                      {fmt(ch.count)}장
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col">
              <div className="flex min-h-[300px] flex-1">
                {/* Y축 눈금 */}
                <div className="relative mr-2 shrink-0" style={{ width: 32 }}>
                  {channelCountScale.ticks.map((t) => (
                    <span
                      key={t}
                      className="absolute right-0 translate-y-1/2 text-[10px] leading-none text-gray-400"
                      style={{
                        bottom: `${(t / channelCountScale.niceMax) * 100}%`,
                      }}
                    >
                      {fmt(t)}
                    </span>
                  ))}
                </div>
                {/* 플롯 영역 */}
                <div className="relative flex-1">
                  {/* 가이드 선 */}
                  {channelCountScale.ticks.map((t) => (
                    <div
                      key={t}
                      className="pointer-events-none absolute inset-x-0 border-t border-gray-100"
                      style={{
                        bottom: `${(t / channelCountScale.niceMax) * 100}%`,
                      }}
                    />
                  ))}
                  {/* 막대 */}
                  <div className="absolute inset-0 flex items-end justify-around">
                    {sentChannels.map((ch) => {
                      const pct =
                        channelCountScale.niceMax > 0
                          ? (ch.count / channelCountScale.niceMax) * 100
                          : 0;
                      return (
                        <div
                          key={ch.channel}
                          className="group relative flex h-full flex-col items-center justify-end"
                          style={{ flex: 1 }}
                        >
                          <div className="pointer-events-none absolute -top-1 z-10 hidden -translate-y-full rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight whitespace-nowrap text-white shadow group-hover:block">
                            {ch.label}: {fmt(ch.count)}장 (
                            {ch.rate.toFixed(1)}%)
                          </div>
                          <span className="mb-1 text-[11px] font-bold text-gray-700">
                            {ch.count > 0 ? fmt(ch.count) : ''}
                          </span>
                          <div
                            className={`w-full max-w-[48px] rounded-t transition-all duration-500 ${ch.color}`}
                            style={{
                              height: `${pct}%`,
                              minHeight: ch.count > 0 ? 4 : 0,
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              {/* X축 라벨 */}
              <div className="flex">
                <div className="mr-2 shrink-0" style={{ width: 32 }} />
                <div className="flex flex-1 justify-around">
                  {sentChannels.map((ch) => (
                    <div
                      key={ch.channel}
                      className="mt-2 text-center"
                      style={{ flex: 1 }}
                    >
                      <span className="block text-[11px] font-medium text-gray-500">
                        {ch.label}
                      </span>
                      <span className="block text-[10px] text-gray-400">
                        {ch.rate.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
